// UndoDelete - biblioteka do obsługi "miękkiego" usuwania z możliwością cofnięcia.
//
// Użycie w widoku:
//   link_to 'Usuń', path,
//     data: { 'undo-delete': true, 'undo-method': 'delete', 'undo-label': 'Rekord usunięty' }
//
// Atrybut data-undo-method zamiast data-method - biblioteka wysyła request samodzielnie przez fetch,
// dzięki czemu jquery_ujs nie przechwytuje linku i nie ma kolizji.
//
// Kontener elementu do ukrycia jest wykrywany automatycznie (tr/li/card),
// lub wskazywany jawnie przez data-undo-item na elemencie nadrzędnym.

var UndoDelete = (function () {
  'use strict';

  // Czas odliczania w milisekundach przed wysłaniem DELETE
  var DELAY = 5000;

  // Mapa oczekujących usunięć: id → { timer, url, method, container, toast }
  var pending = {};
  var counter = 0;
  var _initialized = false;
  var CONTAINER_ID = 'undo-delete-container';

  // ── Planowanie usunięcia ──────────────────────────────────────────────────

  function schedule(linkEl) {
    var id = ++counter;
    var url = linkEl.href;
    var method = (linkEl.dataset.undoMethod || 'delete').toUpperCase();
    var label = linkEl.dataset.undoLabel || 'Rekord usunięty';
    // data-undo-no-hide - tryb dla tranzycji statusów: tylko toast, bez ukrywania kontenera
    var noHide = linkEl.hasAttribute('data-undo-no-hide');
    var container = noHide ? null : findContainer(linkEl);

    if (!noHide && !container) return;

    // Natychmiastowe ukrycie elementu w DOM (tylko gdy nie jesteśmy w trybie no-hide)
    if (container) container.style.display = 'none';
    var toast = showToast(id, label);

    var timer = setTimeout(function () { execute(id); }, DELAY);

    pending[id] = {
      timer: timer,
      url: url,
      method: method,
      container: container,
      toast: toast,
      // noHide=true → po wykonaniu requestu ewaluujemy odpowiedź JS (jak Rails UJS)
      noHide: noHide
    };
  }

  // ── Cofnięcie usunięcia ──────────────────────────────────────────────────

  function undo(id) {
    var e = pending[id];
    if (!e) return;

    clearTimeout(e.timer);
    if (e.container) e.container.style.display = '';
    removeToast(e.toast);
    delete pending[id];
  }

  // ── Wykonanie requestu po upływie czasu ─────────────────────────────────

  function execute(id) {
    var e = pending[id];
    if (!e) return;

    removeToast(e.toast);
    delete pending[id];
    // noHide=true → tryb tranzycji statusu: ewaluujemy odpowiedź JS by zaktualizować DOM
    sendRequest(e.url, e.method, false, e.noHide);
  }

  // ── Flush przed nawigacją (keepalive - request wykona się mimo unload) ───

  function flushAll() {
    Object.keys(pending).forEach(function (id) {
      var e = pending[id];
      clearTimeout(e.timer);
      removeToast(e.toast);
      sendRequest(e.url, e.method, true);
    });
    pending = {};
  }

  // ── Helpery ──────────────────────────────────────────────────────────────

  // Szuka najbliższego elementu-kontenera do ukrycia.
  // Jawny target (data-undo-item) ma priorytet nad auto-detekcją tr/li/card.
  function findContainer(el) {
    return (
      el.closest('[data-undo-item]') ||
      el.closest('tr') ||
      el.closest('li') ||
      el.closest('.card')
    );
  }

  // evalJs=true → odczytuje odpowiedź JS i wykonuje przez <script> tag (bezpieczne przy CSP bez unsafe-eval)
  function sendRequest(url, method, keepalive, evalJs) {
    var csrfEl = document.querySelector('meta[name="csrf-token"]');
    var req = fetch(url, {
      method: method,
      keepalive: !!keepalive,
      headers: {
        'X-CSRF-Token': csrfEl ? csrfEl.content : '',
        'Accept': 'text/javascript, application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (evalJs) {
      req
        .then(function (r) { return r.text(); })
        .then(function (js) {
          if (!js) return;
          var scriptEl = document.createElement('script');
          scriptEl.textContent = js;
          document.head.appendChild(scriptEl);
          scriptEl.remove();
        })
        .catch(function (err) { console.error('[UndoDelete] Błąd odpowiedzi JS:', err); });
    }
  }

  function getOrCreateContainer() {
    var el = document.getElementById(CONTAINER_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = CONTAINER_ID;
      document.body.appendChild(el);
    }
    return el;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // ── Toast ────────────────────────────────────────────────────────────────

  // onUndo - opcjonalny callback wywoływany po kliknięciu "Cofnij".
  // Domyślnie: undo(id), czyli anulowanie oczekującego requestu delete.
  function showToast(id, label, onUndo) {
    var wrap = getOrCreateContainer();

    var toast = document.createElement('div');
    toast.className = 'undo-delete-toast';
    toast.id = 'undo-toast-' + id;
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML =
      '<div class="undo-delete-content">' +
        '<span class="undo-delete-label">' + escapeHtml(label) + '</span>' +
        '<button class="undo-delete-btn" type="button">' +
          'Cofnij' +
        '</button>' +
      '</div>' +
      '<div class="undo-delete-progress">' +
        '<div class="undo-delete-bar" style="animation-duration:' + DELAY + 'ms"></div>' +
      '</div>';

    // Podpinamy listener na button - unikamy inline onclick (CSP friendly)
    toast.querySelector('.undo-delete-btn').addEventListener('click', onUndo || function () {
      undo(id);
    });

    wrap.appendChild(toast);

    // Dwa rAF - daje przeglądarce czas na obliczenie layout przed uruchomieniem transition
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        toast.classList.add('is-visible');
      });
    });

    return toast;
  }

  function removeToast(toast) {
    if (!toast || !toast.parentNode) return;

    toast.classList.remove('is-visible');
    toast.classList.add('is-hiding');

    // Usunięcie z DOM po zakończeniu transition (300ms)
    setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 300);
  }

  // ── Status transitions (natychmiastowy fire + toast z opcją cofnięcia) ──
  //
  // Użycie w widoku:
  //   link_to path, data: { 'status-transition': true }
  //
  // Po udanej odpowiedzi JS serwer wywołuje UndoDelete.showActionToast(label, undoUrl).

  // Pokazuje toast z opcją cofnięcia dla natychmiastowych zmian (np. zmiana statusu).
  // Gdy użytkownik kliknie "Cofnij", wysyłany jest PATCH do undoUrl.
  function showActionToast(label, undoUrl) {
    var id = ++counter;
    var toast = showToast(id, label, function () {
      removeToast(toast);
      sendRequest(undoUrl, 'PATCH', false, true);
    });
    // Auto-ukrycie po DELAY - brak pending requestu do anulowania
    setTimeout(function () { removeToast(toast); }, DELAY);
  }

  // ── Inicjalizacja (idempotentna - bezpieczna przy wielokrotnym wywołaniu) ─

  function init() {
    if (_initialized) return;
    _initialized = true;

    // Event delegation na document - działa dla wszystkich [data-undo-delete],
    // w tym elementów dodawanych dynamicznie (AJAX, offcanvas).
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-undo-delete]');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();
      schedule(link);
    }, true);

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-status-transition]');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();
      sendRequest(link.href, 'PATCH', false, true);
    }, true);

    // Flush przy zamknięciu karty / przeładowaniu strony / nawigacji
    window.addEventListener('beforeunload', flushAll);
    window.addEventListener('pagehide', flushAll);
  }

  // Publiczne API
  return {
    undo: undo,
    flushAll: flushAll,
    init: init,
    showActionToast: showActionToast
  };
})();

// Ekspozycja na window - wymagana by dynamicznie wstrzykiwane skrypty (transition.js.haml)
// mogły wywołać UndoDelete.showActionToast() z globalnego scope (Vite bundluje jako ES moduł).
window.UndoDelete = UndoDelete;

document.addEventListener('DOMContentLoaded', function () { UndoDelete.init(); });
